import { ApeironBlueButton } from '@apeiron/temp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import VerticalAlignBottomIcon from '@mui/icons-material/VerticalAlignBottom'
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop'
import {
  Box,
  ButtonBase,
  styled,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material'
import { OrbitalTrackSetting } from '@src/types/orbitalTrack'
import { ChangeEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

const RowContainer = styled(Box)`
  display: flex;
  padding: 10px 12px;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(217, 217, 217, 0);
  align-items: center;

  :hover {
    background: rgba(217, 217, 217, 0.1);
  }

  ${props => props.theme.breakpoints.down('res480')} {
    flex-wrap: wrap;
    row-gap: 12px;
  }
`

const LeftContainer = styled(Box)`
  display: flex;
  gap: 12px;
  overflow: hidden;

  ${props => props.theme.breakpoints.down('res480')} {
    width: 100%;
    align-items: center;
    justify-content: space-between;
  }
`

const RightContainer = styled(Box)`
  display: flex;
  gap: 12px;
  ${props => props.theme.breakpoints.down('res480')} {
    width: 100%;
    justify-content: end;
  }
`

const CurrentIndex = styled(Typography)`
  color: #febe36;
`

const GroupName = styled(Typography)`
  font-weight: 700;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const IconTextButton = styled(ButtonBase)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
`

const IconText = styled(Typography)`
  font-size: 12px;
  color: #838383;
`

const IndexAndName = styled(Box)`
  display: flex;
  gap: 12px;
  overflow: hidden;
`

const NumberTextField = styled(TextField)`
  width: 60px;
  border: 1px solid #838383;
  border-radius: 4px;

  .MuiInputBase-root {
    height: 100%;
  }

  .MuiInputBase-input.MuiOutlinedInput-input {
    text-align: center;
    padding: 0px 8px;
  }
`

const JumpButton = styled(ApeironBlueButton)`
  height: 100%;
`

export const PriorityTableRow = (props: Props) => {
  const { inviteSetting, onUp, onDown, onTop, onBottom, onJump, realIndex } =
    props

  const { t } = useTranslation()

  const mobileView = useMediaQuery('(max-width: 500px)')

  const [jumpTo, setJumpTo] = useState<string>('')

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setJumpTo(e.target.value)
  }

  const handleJump = () => {
    if (jumpTo === undefined || isNaN(Number(jumpTo))) return

    onJump(Number(jumpTo) - 1)
    setJumpTo('')
  }

  const jumpComponents = (
    <>
      <NumberTextField
        onChange={handleChange}
        value={jumpTo}
        type={'text'}
        placeholder='-'
      />
      <JumpButton onClick={handleJump}>
        {t('common.button.ordering_jump')}
      </JumpButton>
    </>
  )

  return (
    <RowContainer key={inviteSetting.inviteGroupId}>
      <LeftContainer>
        <IndexAndName>
          <CurrentIndex>{realIndex + 1}</CurrentIndex>
          <GroupName>{inviteSetting.groupName}</GroupName>
        </IndexAndName>
        {mobileView && (
          <Box display='flex' gap='5px'>
            {jumpComponents}
          </Box>
        )}
      </LeftContainer>
      <RightContainer>
        <IconTextButton onClick={onUp}>
          <KeyboardArrowUpIcon width='24px' />
          <IconText>{t('common.button.ordering_up')}</IconText>
        </IconTextButton>
        <IconTextButton onClick={onDown}>
          <KeyboardArrowDownIcon width='24px' />
          <IconText>{t('common.button.ordering_down')}</IconText>
        </IconTextButton>
        <IconTextButton onClick={onTop}>
          <VerticalAlignTopIcon width='24px' />
          <IconText>{t('common.button.ordering_top')}</IconText>
        </IconTextButton>
        <IconTextButton onClick={onBottom}>
          <VerticalAlignBottomIcon width='24px' />
          <IconText>{t('common.button.ordering_bottom')}</IconText>
        </IconTextButton>
        {!mobileView && jumpComponents}
      </RightContainer>
    </RowContainer>
  )
}

type Props = {
  inviteSetting: OrbitalTrackSetting
  realIndex: number
  onUp: () => void
  onDown: () => void
  onTop: () => void
  onBottom: () => void
  onJump: (toIndex: number) => void
}
